import React from "react";
import Modal from "../../../../components/Modal";
import CustomInput from "../../../../components/CustomInput";
import CustomSelect from "../../../../components/CustomSelect";
import { StatusBadge } from "../../../../../../constants/statusConstants";
import { ESTIMATE_STATUS_OPTIONS } from "../../../../../../constants/estimateStatus";
import { EditableTierRow, SimpleServiceRow } from "./components/ServiceRow";
import { PendingAdjustmentCard, ReadonlyAdjustmentCard, PendingApprovalAdjustmentCard } from "./components/AdjustmentCard";

const SERVICE_GRID = "grid-cols-[2.5fr_1.5fr_1.5fr_1.5fr]";

const EstimatePopupForm = ({
  isOpen,
  onClose,
  data,
  form,
  setForm,
  editableTiers,
  onUpdateTier,
  adjustmentHistory,
  pendingAdjustment,
  pendingAdjustmentFromApi,
  originalPendingAdjustmentFromApi,
  onAddAdjustment,
  onUpdatePendingAdjustment,
  onUpdatePendingAdjustmentFromApi,
  onRemovePendingAdjustment,
  onDeletePendingAdjustment,
  onDeleteAdjustmentHistory,
  historyLoading,
  historyHasMore,
  onLoadMoreHistory,
  totals,
  isDownloading,
  onDownload,
  onSubmit,
  canEditEstimateStatus,
}) => {
  if (!isOpen || !data) return null;

  const hasTiers = editableTiers.length > 0;
  const hasNewPendingAdjustment = pendingAdjustment !== null;

  const handleVatChange = (value) => {
    let num = Number(value);
    if (isNaN(num)) num = 0;
    const clampedVat = Math.min(10, Math.max(0, num));
    setForm((prev) => ({ ...prev, vatPercent: clampedVat }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Báo phí hồ sơ"
      maxWidth="1000px"
      maxHeight="90vh"
      primaryButtonText="Cập nhật báo phí"
      primaryButtonOnClick={onSubmit}
      cancelButtonText="Hủy"
    >
      <div className="flex flex-col h-full">
        <div className="mb-6">
          <div className={`px-4 grid ${SERVICE_GRID} gap-2 py-3 text-sm font-semibold text-gray-600 border-b`}>
            <div>Tên dịch vụ</div>
            <div>Đơn giá</div>
            <div className="text-center">Số lượng</div>
            <div className="text-right">Thành tiền</div>
          </div>

          {hasTiers ? (
            editableTiers.map((tier, tierIndex) => (
              <EditableTierRow
                key={tier.id}
                tier={tier}
                tierIndex={tierIndex}
                serviceName={data.serviceId?.serviceName}
                onUpdateTier={onUpdateTier}
                isFirstTier={tierIndex === 0}
              />
            ))
          ) : (
            <SimpleServiceRow
              serviceName={data.serviceId?.serviceName}
              unitPrice={data.unitPrice}
              quantity={form.quantity}
              onUpdate={(updates) => setForm({ ...form, ...updates })}
            />
          )}
        </div>

        <div
          className="px-4 flex flex-col gap-1 w-full max-h-[250px] overflow-y-auto mb-4"
          onScroll={(e) => {
            const el = e.currentTarget;
            if (el.scrollHeight - el.scrollTop <= el.clientHeight + 20 && historyHasMore && !historyLoading) {
              onLoadMoreHistory();
            }
          }}
        >
          {adjustmentHistory.map((item) => (
            <ReadonlyAdjustmentCard
              key={item.id}
              item={item}
              onRemove={() => onDeleteAdjustmentHistory(item.id)}
            />
          ))}
          {historyLoading && (
            <div className="flex justify-center py-3">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        <div className="px-6 py-2">
          {pendingAdjustmentFromApi ? (
            <PendingApprovalAdjustmentCard
              adjustment={pendingAdjustmentFromApi}
              originalAdjustment={originalPendingAdjustmentFromApi}
              onUpdate={onUpdatePendingAdjustmentFromApi}
              onRemove={onDeletePendingAdjustment}
            />
          ) : !pendingAdjustment ? (
            <button
              onClick={onAddAdjustment}
              className="border border-dashed border-[#D2D5DA] w-fit py-2 px-4 text-[#2E60F3] bg-[#F1F6FE] rounded-[8px] hover:bg-blue-50 hover:border-blue-400 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
            >
              <span>Thêm điều chỉnh</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </button>
          ) : (
            <PendingAdjustmentCard
              adjustment={pendingAdjustment}
              onUpdate={onUpdatePendingAdjustment}
              onRemove={onRemovePendingAdjustment}
            />
          )}
        </div>

        <div className="px-6 border-t pt-4 bg-white sticky bottom-0 z-10">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-700 font-medium">Tạm tính</span>
              <span className="font-semibold">{totals.subtotal.toLocaleString()} ₫</span>
            </div>

            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-700 font-medium">VAT (%)</span>
              <div className="w-[80px] ml-auto">
                <CustomInput
                  type="number"
                  value={String(form?.vatPercent ?? 0)}
                  onChange={handleVatChange}
                  placeholder="%"
                  height="36px"
                  textAlign="right"
                  required
                  suffix="%"
                />
              </div>
            </div>

            <div className="flex justify-between items-center text-lg border-t pt-2">
              <span className="text-gray-800 font-bold">Tổng cộng</span>
              <span className="font-bold text-[#0267FF]">
                {totals.total.toLocaleString()} ₫
              </span>
            </div>
          </div>

          <div className="mt-2 flex gap-6 items-end justify-between">
            <div className="flex-1 max-w-[340px]">
              <label className="block text-sm font-medium text-gray-600 mb-2">Trạng thái hồ sơ</label>
              {hasNewPendingAdjustment ? (
                <StatusBadge status={form?.status?.label} />
              ) : canEditEstimateStatus ? (
                <CustomSelect
                  options={ESTIMATE_STATUS_OPTIONS}
                  value={form?.status}
                  onChange={(opt) => setForm({ ...form, status: opt })}
                  placeholder="Chọn trạng thái"
                  height="43px"
                  required
                />
              ) : (
                <StatusBadge status={form.status?.label} />
              )}
            </div>

            <button
              onClick={onDownload}
              disabled={isDownloading}
              className="flex items-center gap-2 py-2 px-6 rounded-lg border border-blue-600 text-blue-600 font-medium hover:bg-blue-50 transition-colors disabled:opacity-50 h-[43px]"
            >
              {isDownloading ? (
                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M17.5 12.5V15.8333C17.5 16.2936 17.1269 16.6667 16.6667 16.6667H3.33333C2.8731 16.6667 2.5 16.2936 2.5 15.8333V12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M5 8.33337L10 13.3334L15 8.33337" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M10 13.3334V3.33337" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              <span>Tải xuống PDF</span>
            </button>
          </div>

          <p className="my-4 text-xs text-red-500 italic">
            * Lưu ý: Chi phí này mang tính chất tham khảo, chi phí chính xác sẽ được hệ thống cập nhật lại.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default EstimatePopupForm;
